import { useCallback, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { AssignedConversation } from "@doktor-se/bones-ui/dist/web-shared/types"
import { formatPnr, timeIsInPast } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { reassignBooking, reassignConversation } from "api"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { BookedAppointment } from "reducers/conversations/conversations.reducer"

import { BooleanCell, DateCell, NameCell } from "components/v2/conversationList/Cells"
import ConfirmDialog, {
  ConfirmDialogProps
} from "components/v2/conversationList/components/ConfirmDialog/ConfirmDialog"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

import styles from "./RequestsList.module.scss"

interface RequestsListProps {
  conversations?: BookedAppointment[]
  columns?: Array<{ key: string; name: string }>
  items?: ConversationListItem<BookedAppointment>[]
}

interface RequestsListItem extends ConversationListItem<BookedAppointment> {}

const RequestsList = ({ conversations, columns: customColumns, items: customItems }: RequestsListProps) => {
  const intl = useIntl()
  const user = useAppSelector(state => state.auth.user)
  const dispatch = useAppDispatch()
  const kinds = useAppSelector(state => state.booking.bookingTypes)
  const appLanguage = useAppSelector(state => state.app.language)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<BookedAppointment>()

  const dialogProps: Omit<ConfirmDialogProps, "isOpen"> = {
    title: intl.formatMessage({ id: "requests.cancel.header" }),
    message: intl.formatMessage({ id: "requests.cancel.description" }),
    conversation: selectedConversation,
    onClose: () => setDialogOpen(false),
    onConfirm: async () => {
      const bookingId = selectedConversation?.metadata?.bookingId
      if (bookingId) {
        await dispatch(
          reassignBooking({
            booking_ids: [bookingId],
            staff_id: selectedConversation?.assignedStaffId || undefined
          })
        )
      } else {
        dispatch(
          reassignConversation({
            conversation: selectedConversation as AssignedConversation,
            assignedId: user?.id!
          })
        )
      }
    },
    confirmText: intl.formatMessage({ id: "requests.cancel.approve" })
  }

  const defaultColumns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "status",
      name: intl.formatMessage({ id: "label.overview.status" })
    },
    {
      key: "kind",
      name: intl.formatMessage({ id: "label.overview.booking_type" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.booked_time" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const mapConversationListItem = useCallback(
    (conv: BookedAppointment): RequestsListItem => {
      const isChildConversation = conv.profile?.type === "child"
      const { kind_id: kindId } = !!conv.bookingFilter ? JSON.parse(conv.bookingFilter) : {}
      const isPast = !!conv.bookingStart ? timeIsInPast(conv.bookingStart) : false
      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          status: conv.reassignTo === user?.id ? "waiting_for_you" : "waiting_for_colleague",
          kind: kindId ? kinds.find(k => k.id === kindId)?.localisedNames[appLanguage] : "-",
          start: <DateCell past={isPast} value={conv.bookingStart} />,
          listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
          recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
          medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {}),
          status: conv.reassignTo === user?.id ? "waiting_for_you" : "waiting_for_colleague",
          kind: kindId ? sortValueFunctions.bookingType(conv, { bookingTypes: kinds, appLanguage }) : "",
          start: sortValueFunctions.bookingStart(conv)
        }
      }
    },
    [appLanguage, kinds, user?.id]
  )

  const defaultList = useMemo(
    () => conversations?.map(conv => mapConversationListItem(conv)) || [],
    [conversations, mapConversationListItem]
  )

  // Use custom props if provided, otherwise use defaults
  const columns = customColumns || defaultColumns
  const items = customItems || defaultList

  const openCancelDelegationDialog = (item: RequestsListItem) => {
    setSelectedConversation(item.conversation)
    setDialogOpen(true)
  }

  return (
    <>
      <ConversationTable
        title={intl.formatMessage({ id: "label.overview.requests" })}
        columns={columns}
        items={items}
        onItemClick={openCancelDelegationDialog}
      />
      <ConfirmDialog isOpen={dialogOpen} {...dialogProps} />
      <div className={styles.divider} />
    </>
  )
}

export default RequestsList
